import React, { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import posthog from "posthog-js"
import { CoolButton } from "../components/CoolButton.jsx"
import { LanguageSelector } from "../components/LanguageSelector.jsx"
import { Loader } from "../components/LoadingToast.jsx"
import { TopMenu } from "../components/TopMenu.jsx"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider.jsx"
import { useLinkSubscriptionToStore } from "../hooks/usePaddleHooks.js"
import { useMediaQuery } from "../hooks/useMediaQuery.js"

// Shown when a store is connected but none of the user's subscriptions is
// bound to it. Binds one of their unlinked subscriptions in one click; before
// this screen existed the app sent them back through the Shopify install.
const ActivateStorePlan = ({ onActivated }) => {
	const isMobile = useMediaQuery("(max-width: 500px)")
	const { t } = useTranslation()
	const { state } = useContext(GlobalStateContext)
	const linkSubscriptionToStore = useLinkSubscriptionToStore()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)

	const store = state.shop
	const candidate = [
		...(state.otherKopyBasicSubscriptions || []),
		...(state.otherKopyProtectSubscriptions || []),
	].find(
		(subscription) => subscription?.id && !subscription?.custom_data?.store,
	)

	const activate = async () => {
		if (loading) return
		setError(null)

		if (!candidate) {
			posthog?.capture("store_plan_activate_failed", {
				store,
				reason: "no_unlinked_subscription",
			})
			setError(t("authentication.activate_plan_failed"))
			return
		}

		setLoading(true)
		const { success, error: linkError } = await linkSubscriptionToStore({
			subscriptionId: candidate.id,
			store,
		})
		setLoading(false)

		if (!success) {
			posthog?.capture("store_plan_activate_failed", {
				store,
				reason: linkError || "unknown",
			})
			setError(t("authentication.activate_plan_failed"))
			return
		}
		posthog?.capture("store_plan_activated", { store })

		await onActivated?.()
	}

	return (
		<div
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				padding: "0rem 1rem",
			}}
		>
			<TopMenu accountSelector storeSelector />

			<div
				className="border shadow"
				style={{
					marginTop: "5rem",
					marginBottom: "5rem",
					maxWidth: isMobile ? "100%" : "480px",
					width: "100%",
					padding: "1.5rem",
					background: "#fff",
					borderRadius: "2rem",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
				}}
			>
				<h1
					style={{
						fontSize: "1.6rem",
						fontWeight: "600",
						color: "#222",
						textAlign: "center",
					}}
				>
					{t("authentication.activate_plan_title")}
				</h1>
				<p
					style={{
						fontSize: "0.8rem",
						fontWeight: "400",
						color: "#555",
						textAlign: "center",
						marginBottom: "2rem",
					}}
				>
					{t("authentication.activate_plan_description", {
						shop: store,
					})}
				</p>

				<CoolButton
					innerPadding={"0.5rem 2rem"}
					fontSize="0.8rem"
					colored={true}
					disabled={loading}
					onClick={activate}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "0.3rem",
						}}
					>
						{loading ? <Loader size="0.9rem" color="#fff" /> : ""}
						<p
							style={{
								fontSize: "0.75rem",
								fontWeight: "500",
								color: "#fff",
							}}
						>
							{t("authentication.activate_plan_button")}
						</p>
					</div>
				</CoolButton>

				{error ? (
					<div
						style={{
							marginTop: "0.8rem",
							color: "red",
							fontSize: "0.8rem",
							fontWeight: "500",
							textAlign: "center",
						}}
					>
						{error}
					</div>
				) : (
					""
				)}
			</div>

			<div
				style={{
					position: "fixed",
					bottom: "2rem",
					left: "2rem",
					zIndex: "500",
				}}
			>
				<LanguageSelector />
			</div>
		</div>
	)
}

export default ActivateStorePlan
